import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Dialog, // For delete confirmation
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { AddIcon, EditIcon, DeleteIcon, AutorenewIcon } from '../../components/icons'; // Assuming these icons exist
import { ClusterFormModal, ClusterFormData } from '../../components/ClusterFormModal'; // Import the modal
import { Rule } from '../../components/AutoTopUpConfigModal'; // Import Rule type for cluster data

// Define Cluster type matching the DB and form data needs
type Cluster = {
  id: string;
  organization_id: string;
  name: string;
  use_case_type: string;
  is_critical: boolean;
  auto_topup_enabled: boolean;
  auto_topup_config: Rule[] | null;
  created_at: string;
  updated_at: string;
  default_sim_status: string;
  usage_limit_value: number | null;
  sim_count?: number; // Add count of SIMs
};

export function ClusterManagement() {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<ClusterFormData | null>(null);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cannotDeleteInfoOpen, setCannotDeleteInfoOpen] = useState(false);
  const [clusterToDelete, setClusterToDelete] = useState<Cluster | null>(null);
  const [assignedSimCount, setAssignedSimCount] = useState<number>(0);
  const [checkingSims, setCheckingSims] = useState<boolean>(false);

  useEffect(() => {
    fetchOrganizationAndClusters();
  }, []);

  const fetchOrganizationAndClusters = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Get User and Organization ID
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw userError || new Error('User not found.');

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();
      if (profileError || !profileData) throw profileError || new Error('User profile or organization not found.');
      setOrganizationId(profileData.organization_id);

      // 2. Fetch Clusters for that Organization - explicitly select new columns
      const { data: clusterData, error: clusterError } = await supabase
        .from('clusters')
        // Select existing columns and the new auto_topup fields + default_sim_status
        .select('id, organization_id, name, use_case_type, is_critical, auto_topup_enabled, auto_topup_config, default_sim_status, usage_limit_value, created_at, updated_at')
        .eq('organization_id', profileData.organization_id)
        .order('name'); 

      if (clusterError) throw clusterError;
      
      // Cast the fetched data to the updated Cluster type
      const fetchedClusters = (clusterData as Cluster[]) || [];
      
      // 3. Fetch SIM counts for each cluster
      if (fetchedClusters.length > 0) {
        // Process each cluster individually with proper count syntax
        for (const cluster of fetchedClusters) {
          const { count, error } = await supabase
            .from('sim_cards')
            .select('*', { count: 'exact', head: true })
            .eq('cluster_id', cluster.id);
          
          if (error) {
            console.error(`Error getting count for cluster ${cluster.id}:`, error);
            cluster.sim_count = 0;
          } else {
            cluster.sim_count = count || 0;
          }
        }
      }
      
      setClusters(fetchedClusters);

    } catch (err: any) {
      console.error('Error fetching clusters:', err);
      setError(err.message || 'Failed to load cluster data.');
      setClusters([]); // Clear data on error
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cluster: Cluster | null = null) => {
    // Map Cluster to ClusterFormData for the modal
    const formData = cluster ? { ...cluster } : null;
    setSelectedCluster(formData);
    setModalOpen(true);
  };

  const handleCloseModal = (refresh = false) => {
    setModalOpen(false);
    setSelectedCluster(null);
    if (refresh) {
      fetchOrganizationAndClusters(); // Refresh the list if changes were saved
    }
  };

  const handleOpenDeleteConfirm = async (cluster: Cluster) => {
    setClusterToDelete(cluster);
    setCheckingSims(true);
    setError(null);
    try {
      const { count, error: countError } = await supabase
        .from('sim_cards')
        .select('id', { count: 'exact', head: true })
        .eq('cluster_id', cluster.id);

      if (countError) throw countError;

      if (count === 0) {
        setAssignedSimCount(0);
        setCannotDeleteInfoOpen(false);
        setDeleteConfirmOpen(true);
      } else {
        setAssignedSimCount(count || 0);
        setDeleteConfirmOpen(false);
        setCannotDeleteInfoOpen(true);
      }
    } catch (err: any) {
      console.error("Error checking assigned SIMs:", err);
      setError(err.message || 'Failed to check assigned SIMs.');
    } finally {
      setCheckingSims(false);
    }
  };

  const handleCloseDeleteConfirm = () => {
    setClusterToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleCloseCannotDeleteInfo = () => {
    setClusterToDelete(null);
    setCannotDeleteInfoOpen(false);
  };

  const handleDeleteCluster = async () => {
    if (!clusterToDelete) return;
    setLoading(true);
    try {
      const { error: deleteError } = await supabase
        .from('clusters')
        .delete()
        .eq('id', clusterToDelete.id);

      if (deleteError) throw deleteError;

      handleCloseDeleteConfirm();
      fetchOrganizationAndClusters();
    } catch (err: any) {
      console.error('Error deleting cluster:', err);
      setError(err.message || 'Failed to delete cluster.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Cluster Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()} // Open modal for creation (no cluster data)
          disabled={!organizationId} // Disable if org ID not loaded
        >
          Create New Cluster
        </Button>
      </Box>

      {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading && !deleteConfirmOpen && !cannotDeleteInfoOpen ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Use Case Type</TableCell>
                  <TableCell>Settings</TableCell>
                  <TableCell>Default SIM Status</TableCell>
                  <TableCell>Usage Limit</TableCell>
                  <TableCell>SIM Count</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clusters.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No clusters found. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  clusters.map((cluster) => (
                    <TableRow key={cluster.id}>
                      <TableCell>{cluster.name}</TableCell>
                      <TableCell>{cluster.use_case_type}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Criticality Status">
                            <Chip
                              label={cluster.is_critical ? 'Critical' : 'Normal'}
                              color={cluster.is_critical ? 'error' : 'default'}
                              size="small"
                            />
                          </Tooltip>
                          {/* Add Auto Top-Up Indicator - Style like SIM Management */}
                          {cluster.auto_topup_enabled && (
                            <Tooltip title={`Configure Cluster Auto Top-Up Rules`}>
                               <Chip 
                                  icon={<AutorenewIcon fontSize="small" />} 
                                  // Show rule count in label
                                  label={`${cluster.auto_topup_config?.length || 0} rules`} 
                                  size="small"
                                  color="primary"
                                  // Removed variant="outlined"
                                  // Make clickable to open edit modal
                                  onClick={() => handleOpenModal(cluster)} 
                                  sx={{ cursor: 'pointer' }} 
                               />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cluster.default_sim_status === 'active' ? 'Active' : 'Inactive'}
                          color={cluster.default_sim_status === 'active' ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={cluster.usage_limit_value === null ? 'No Limit' : `R${cluster.usage_limit_value}`}
                          color={cluster.usage_limit_value === 0 ? 'error' : (cluster.usage_limit_value ? 'primary' : 'default')}
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenModal(cluster)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        {cluster.sim_count !== undefined ? cluster.sim_count : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit Cluster">
                          <IconButton size="small" onClick={() => handleOpenModal(cluster)}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Cluster">
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenDeleteConfirm(cluster)} 
                              color="error"
                              disabled={checkingSims && clusterToDelete?.id === cluster.id}
                            >
                              {checkingSims && clusterToDelete?.id === cluster.id 
                                ? <CircularProgress size={20} color="inherit"/> 
                                : <DeleteIcon fontSize="inherit" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Render the Form Modal */}
      {organizationId && (
          <ClusterFormModal
            open={modalOpen}
            onClose={handleCloseModal}
            cluster={selectedCluster}
            organizationId={organizationId}
          />
      )}

      {/* Original Delete Confirmation Dialog (Only opens if count is 0) */} 
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Delete Cluster?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the cluster "{clusterToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteCluster} color="error" disabled={loading}> 
            {loading ? <CircularProgress size={20} color="inherit"/> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* New "Cannot Delete" Info Dialog */}
       <Dialog open={cannotDeleteInfoOpen} onClose={handleCloseCannotDeleteInfo}>
        <DialogTitle>Cannot Delete Cluster</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
             Cannot delete cluster "{clusterToDelete?.name}" because it currently has {assignedSimCount} assigned SIM{assignedSimCount !== 1 ? 's' : ''}.
          </Alert>
          <DialogContentText>
            Please reassign all SIMs from this cluster before attempting to delete it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCannotDeleteInfo} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
} 